import {ChangeDetectionStrategy, Component, effect, input, output, signal, Signal, WritableSignal} from "@angular/core";
import {NgTemplateOutlet} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {DokumenterTilSigneringModell, DokumentId, DokumentSignatarerModell, DokumentSignatarModell, DokumentTilSigneringModell, DokumentVersjonModell, GruppeId, MøteModellEnkel, SigneringStatus, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {signatarerSomHarSignert} from "felles-ng2/modell/dokumentSignatarerModellHjelpere";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {DokumentSigneringInformasjon} from "felles-ng2/tjenester/DokumentSigneringInformasjon";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatTooltip} from "@angular/material/tooltip";
import {TypetNgTemplateDirektiv} from "web-ng2/delt/direktiver/typetNgTemplate/typetNgTemplate";
import {fadeInnAnimasjon} from "web-ng2/delt/animasjoner";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {lesZonedDateTimeSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import {erArkivdokument} from "felles-ng2/modell/dokumentModellHjelpere";
import {SigneringSendPåminnelseDialogKomponent} from "web-ng2/moduler/signering/modale/signering-send-påminnelse-dialog/signering-send-påminnelse-dialog";
import {MatDialog} from "@angular/material/dialog";
import {SigneringAvsluttDialogKomponent} from "web-ng2/moduler/signering/modale/signering-avslutt-dialog/signering-avslutt-dialog";

/** Panel som viser oversikt over utestående signeringer */
@Component({
    selector: 'dokumenter-til-signering-popup',
    templateUrl: 'dokumenter-til-signering-popup.html',
    styleUrl: 'dokumenter-til-signering-popup.scss',
    animations: [fadeInnAnimasjon('100ms')],
    standalone: true,
    imports: [TranslateModule, MatIcon, MatIconButton, NgTemplateOutlet, ZonedDateTimeSomPlainDatePipe, FormaterPlainDatePipe, MatButton, AvatarKomponent, MatTooltip, TypetNgTemplateDirektiv],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DokumenterTilSigneringPopupKomponent {

    vedLukk = output<void>();

    dokumenterBrukerIkkeHarSignert = input.required<DokumenterTilSigneringModell>();
    dokumenterAndreSkalSignere = input.required<DokumenterTilSigneringModell>();
    maksPopupHoeyde = input<number>(0);

    harSkrivetilgang = toSignal(this.kontekst.autentisertBrukerSomStrøm().pipe(map(bruker => bruker.inneharTilgangsrolleINoenGruppe(TilgangsrolleMedlem.skrive))), {initialValue: false});

    /** Definerer typer for felter som er tilgjengelig i NgTemplate dokumenterTilSigneringAvAndreFragment og dokumenterTilSigneringAvBrukerFragment */
    dokumenterTilSigneringFragmentKontekstType!: { dokumenter: DokumentTilSigneringModell<DokumentVersjonModell>[]; };
    /** Definerer typer for felter som er tilgjengelig i NgTemplate signaturstatusFragment */
    signaturstatusFragmentKontekstType!: { signatarer: DokumentSignatarerModell; dokumentId: DokumentId; dokumentversjon: DokumentVersjonModell | null; };
    /** Definerer typer for felter som er tilgjengelig i NgTemplate avsluttSigneringTipsFragment */
    avsluttSigneringTipsFragmentKontekstType!: { signatarer: DokumentSignatarerModell; dokumentversjon: DokumentVersjonModell; };

    /** dersom det er mer enn dette antall dager siden dok ble sendt til signering vises tips om avslutning av signering */
    private avsluttSigneringstipsFrist = 60;
    private signaturStatusVises = new Map<string, WritableSignal<boolean>>();

    constructor(private navigasjon: Navigasjon,
                private dokumentSigneringInformasjon: DokumentSigneringInformasjon,
                private oversettelser: Oversettelser,
                private kontekst: BrukerkontekstTilbyder,
                private klokke: Klokke,
                private matDialog: MatDialog) {

        //sørg for at alle dokumenter har et innslag i signaturStatusVises
        effect(() => [...this.dokumenterAndreSkalSignere().moeter, ...this.dokumenterAndreSkalSignere().saksdokumenter, ...this.dokumenterAndreSkalSignere().arkivdokumenter]
          .map(dokument => dokument.tilSignering.id)
          .filter(id => !this.signaturStatusVises.has(id))
          .forEach(id => this.signaturStatusVises.set(id, signal(false))));
    }

    gruppeinfo(gruppeId: GruppeId): string {
        const gruppe = this.kontekst.autentisertBruker().finnAutentiserteGruppe(gruppeId);
        return erIkkeNil(gruppe) ?
          this.oversettelser.oversett(i18n('i {tittel} i {organisasjonsnavn}'), {tittel: gruppe.tittel, organisasjonsnavn: gruppe.organisasjonsnavn}) :
          '';
    }

    antallSomHarSignert(signatarer: DokumentSignatarerModell): number {
        return signatarerSomHarSignert(signatarer).length;
    }

    viserAvsluttSigneringstips(dokument: DokumentTilSigneringModell<DokumentVersjonModell>): boolean {
        return this.avsluttSigneringMulig(dokument.tilSignering) && this.dagerSidenSendtTilSignering(dokument.signatarer) > this.avsluttSigneringstipsFrist;
    }

    dagerSidenSendtTilSignering(signatarer: DokumentSignatarerModell): number {
        const invitasjonSendt = lesZonedDateTimeSerialisertOpt(signatarer.signeringsInvitasjonSendt);
        if (erNil(invitasjonSendt)) return 0;
        return Math.floor(this.klokke.nåSomZonedDateTime().since(invitasjonSendt).total({unit: 'days'}));
    }

    viserSignaturstatusSignal(id: string): Signal<boolean> {
        return this.signaturStatusVises.get(id) ?? signal(false); //bruker ?? i tilfelle angitt id mot formodning ikke skulle være lagt til i map
    }

    visEllerSkjulSignaturstatus(id: string) {
        this.signaturStatusVises.get(id)?.update(vises => !vises);
    }

    signeringsinformasjon(signatar: DokumentSignatarModell) {
        return this.dokumentSigneringInformasjon.signaturinformasjonSomTekst(signatar.signaturinformasjon!);
    }

    purringSendeOmAntallDager(signatarer: DokumentSignatarerModell) {
        if (!signatarer) return '';
        return signatarer.dagerTilNestePurring == 0 ?
          this.oversettelser.oversett(i18n('Påminnelse sendes i dag')) :
          this.oversettelser.oversett(i18n('Påminnelse sendes om {antall} dager'), {antall: signatarer.dagerTilNestePurring});
    }

    avsluttSigneringMulig(dokumentversjon: DokumentVersjonModell | null): boolean {
        return dokumentversjon?.signeringStatus == SigneringStatus.delvisSignert;
    }

    sendPurring(dokumentId: DokumentId) {
        SigneringSendPåminnelseDialogKomponent.vis(this.matDialog, dokumentId);
    }

    avsluttSignering(dokumentversjon: DokumentVersjonModell) {
        SigneringAvsluttDialogKomponent.vis(this.matDialog, dokumentversjon);
    }

    gaaTilProtokoll(møte: MøteModellEnkel) {
        this.navigasjon.gåTilMøteProtokoll(møte.id);
    }

    gaaTilDokumentForSignering(dokument: DokumentVersjonModell) {
        if (erArkivdokument(dokument))
            this.navigasjon.gåTilSignerArkivdokument(dokument.dokumentId);
        else
            this.navigasjon.gåTilSignerSaksdokument(dokument.dokumentId);
    }

    gaaTilDokument(dokument: DokumentVersjonModell) {
        if (erArkivdokument(dokument))
            this.navigasjon.gåTilArkivdokumentKnyttetTilDokument(dokument.dokumentId);
        else
            this.navigasjon.gåTilSakKnyttetTilDokumentVersjon(dokument.id);
    }

    lukk() {
        this.vedLukk.emit();
    }
}
